<template>
  <!-- `embedded` drops the card and the buttons for a modal, which supplies
       both (AM-1107). -->
  <component :is="embedded ? 'div' : 'gg-card'" id="create-filter">
    <gg-card-header v-if="!embedded">
      {{ componentTitle }}
    </gg-card-header>
    <component :is="embedded ? 'div' : 'gg-card-text'">
      <v-form>
        <v-row>
          <v-col cols="12">
            <gg-text-field outlined
                           label="Filter Name"
                           v-model="model.filtername"
                           v-validate="'required'"
                           data-vv-name="filtername"
                           data-vv-as="Filter name"
                           :error-messages="errors.collect('filtername')"></gg-text-field>
            The filter name must be unique. If you give the filter the same name as another filter, the previous
            filter will be overwritten.
          </v-col>
          <v-col cols="12">
            <gg-input-label>Rules</gg-input-label>
            <div v-for="(row, index) in rules" :key="row.id">
              <rules
                :index="row"
                :index_in_array="index"
                :array_length="rules.length"
                :rule="editedRuleData[row.id - 1]"
                @add="addRuleItem('rule')"
                @remove="removeRuleItem('rule', row)"
                @sort_up="moveFilter"
                @sort_down="moveFilter"
                @changed="setRules"
              />
              <v-divider></v-divider>
            </div>
          </v-col>
          <v-col cols="12">
            <gg-input-label>Actions</gg-input-label>
            <div v-for="row in rule_actions" :key="row.id">
              <actions
                :index="row"
                :action="editedActionData[row.id - 1]"
                @add="addRuleItem('action')"
                @remove="removeRuleItem('action', row)"
                @changed="setRules"
              />
            </div>
          </v-col>
          <v-col v-if="!embedded" cols="12" class="mt-0 pt-0">
            <gg-btn tile
                    class="mr-2"
                    elevation="0"
                    color="primary"
                    @click="addRule"
                    :disabled="loading"
                    :loading="loading">
              {{ buttons.add }}
            </gg-btn>
            <gg-btn text
                    elevation="0"
                    color="primary"
                    @click="onReset">
              {{ buttons.reset }}
            </gg-btn>
          </v-col>
        </v-row>
      </v-form>
    </component>
  </component>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Rules from './Rules'
import Actions from './Actions'

export default {
  name: 'CreateFilter',
  components: {
    Rules, Actions
  },
  props: {
    // The address the filter belongs to. Read from the route when not given.
    account: {
      type: String,
      default: '',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    // For a page that shows the form only while creating or editing: the
    // second button reads Cancel and emits `cancel` instead of clearing.
    cancelable: {
      type: Boolean,
      default: false,
    },
    // Mounted after the filter to edit was loaded, so the `filters.single`
    // watcher will not fire; parse it straight away instead.
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    model: {
      filtername: '',
      oldfiltername: '',
      action: [],
      dest: [],
      match: [],
      opt: [],
      part: [],
      val: []
    },
    rules: [{ id: 1 }],
    rule_actions: [{ id: 1 }],
    mode: 'Add', // can be add or save
    editedRuleData: [],
    editedActionData: [],
    loading: false
  }),
  computed: {
    ...mapGetters({
      filters: 'email/filters',
    }),
    // A query parameter on the hosting route, a path parameter on the Email
    // section's `filters/:account`.
    targetAccount () {
      return this.account || this.$route.query.account || this.$route.params.account
    },
    newRuleId () {
      // Creating a new id for rule
      return this.rules.length === 0 ? 1 : Math.max(...this.rules.map(r => r.id)) + 1
    },
    newActionId () {
      // Creating a new id for action
      return this.rule_actions.length === 0 ? 1 : Math.max(...this.rule_actions.map(r => r.id)) + 1
    },
    componentTitle () {
      const filterName = this.filters.single.filtername

      return this.mode === 'Add' ? 'Create Filter' : `Editing "${filterName}"`
    },
    buttons () {
      if (this.mode === 'Add') {
        return {
          add: 'Add Filter',
          reset: this.cancelable ? 'Cancel' : 'Reset'
        }
      } else {
        return {
          add: 'Save Filter',
          reset: 'Cancel Edit'
        }
      }
    }
  },
  methods: {
    ...mapActions({
      addNewFilter: 'email/addNewFilter',
      setFilters: 'email/setFilters',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError',
    }),
    async getData () {
      try {
        await this.setFilters({ account: this.targetAccount, _username: this.$route.query.user })
      } catch (e) {
        this.reportError(e)
      }
    },

    /*
      @type - can be empty or 'rule'
      if 'rule' method create a new copy of RulesInput component
      else it will create a copy of RulesAction component
       */
    addRuleItem (type) {
      if (type === 'rule') {
        this.rules.push({ id: this.newRuleId })
      } else {
        this.rule_actions.push({ id: this.newActionId })
      }
    },

    /*
      @type - can be empty or 'rule'
      @row - object with id from computed property
      Delete rule or action, depends of type argument
       */
    removeRuleItem (type, row) {
      if (type === 'rule') {
        if (this.rules.length !== 1) {
          this.rules.splice(this.rules.indexOf(row), 1)
        }
      } else {
        if (this.rule_actions.length !== 1) {
          this.rule_actions.splice(this.rule_actions.indexOf(row), 1)
        }
        this.model.action.splice(row.id - 1, 1)
      }
    },

    /*
      @payload - data from event
      Sorting rules items
    */
    moveFilter (payload) {
      let newIndex = payload.new_index
      let oldIndex = payload.old_index

      let oldvalue = ''
      let newvalue = ''

      if (newIndex !== -1 && this.rules.length !== newIndex) {
        if (newIndex >= this.rules.length) {
          let k = newIndex - this.rules.length + 1
          while (k--) {
            this.rules.push(undefined)
          }
        }
        this.rules.splice(newIndex, 0, this.rules.splice(oldIndex, 1)[0])
      }

      // Sort model proprty
      for (let key in this.model) {
        if (this.model.hasOwnProperty(key)) {
          if (key === 'filtername' || key === 'oldfiltername' || key === 'action' || key === 'dest') { continue }

          for (let key1 in this.model[key][oldIndex]) {
            if (this.model[key][oldIndex].hasOwnProperty(key1)) {
              if (key1 === 'index') { continue }

              for (let key2 in this.model[key][newIndex]) {
                if (this.model[key][newIndex].hasOwnProperty(key2)) {
                  if (key2 === 'index') { continue }

                  oldvalue = this.model[key][oldIndex][key1]
                  newvalue = this.model[key][newIndex][key2]

                  this.model[key][oldIndex][key1] = newvalue
                  this.model[key][newIndex][key2] = oldvalue
                }
              }
            }
          }
        }
      }
    },

    setRules (payload) {
      let index = payload.index
      // Function to check if rule already added
      const search = (what, array) => array.find(element => element.index === what)

      const rules = payload

      // Iterating over the rules object
      for (let prop in rules) {
        // If object key === index, skip it
        if (prop === 'index') { continue }

        // If in model array rule was already added
        if (search(index, this.model[prop])) {
          // Then just edit it
          this.model[prop][index - 1][rules[prop].rule_name] = rules[prop].value

          if ((this.model[prop][index - 1][rules[prop].rule_name]).indexOf('val') === 0) {
            this.model[prop][index - 1][rules[prop].isReady] = rules[prop].ready
          }
        } else {
          let rule = {
            index: index
          }
          rule[rules[prop].rule_name] = rules[prop].value

          // It there is not rule yet, just add it
          this.model[prop].push(rule)
        }
      }
    },

    // Method to create or edit filters
    async addRule () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.loading = true
      this.$emit('saving', true)
      const data = {
        uapi_module: 'Email',
        uapi_function: 'store_filter',
        account: this.targetAccount,
        _username: this.$route.query.user,
      }
      try {
        for (let key in this.model) {
          if (this.model.hasOwnProperty(key)) {
            if (key === 'filtername') { data[key] = this.model[key] }
            if (key === 'oldfiltername') { data[key] = this.model[key] }
            if (key === 'filtername' || key === 'oldfiltername') { continue }

            for (let i = 0; i < this.model[key].length; i++) {
              for (let key1 in this.model[key][i]) {
                if (this.model[key][i].hasOwnProperty(key1)) {
                  if (key1 === 'index') { continue }
                  data[key1] = this.model[key][i][key1]
                }
              }
            }
          }
        }
        await this.addNewFilter(data, this.targetAccount)
        this.loading = false
        this.$emit('saving', false)
        this.reportSuccess('You have successfully stored a filter.')
        this.resetForm(false)
        this.$emit('stored', data.account)
      } catch (e) {
        this.loading = false
        this.$emit('saving', false)
        this.reportError(e)
      }
    },

    // Method for parsing data when somebody edit filter
    parseEditedData () {
      const filter_data = this.filters.single

      this.rules = []
      this.rule_actions = []
      this.editedRuleData = []
      this.editedActionData = []

      this.mode = 'Save'

      this.model.filtername = filter_data.filtername
      this.model.oldfiltername = filter_data.filtername

      let rule = []
      let action = []

      // Parsing data and push it to prop
      for (let key in filter_data) {
        if (filter_data.hasOwnProperty(key)) {
          if (key === 'filtername') { continue }
          if (key === 'rules') {
            for (let i = 0; i < filter_data[key].length; i++) {
              rule.push(filter_data[key][i])
              this.$nextTick(() => {
                this.addRuleItem('rule')
              })
            }
          }
          if (key === 'actions') {
            for (let i = 0; i < filter_data[key].length; i++) {
              action.push(filter_data[key][i])
              this.$nextTick(() => {
                this.addRuleItem()
              })
            }
          }
        }
      }

      this.editedRuleData = rule
      this.editedActionData = action
    },

    onReset () {
      if (this.cancelable) {
        this.resetForm(false)
        this.$emit('cancel')
      } else {
        this.resetForm()
      }
    },

    // Reseting form
    resetForm (isMsg = true) {
      // Clear filter name
      this.model.filtername = ''
      this.model.oldfiltername = ''
      this.mode = 'Add'

      // Clear a whole model
      for (let key in this.model) {
        if (key === 'filtername' || key === 'oldfiltername') { continue }
        if (this.model.hasOwnProperty(key)) {
          this.model[key] = []
        }
      }

      // Clear rules & actions array
      this.rules = []
      this.rule_actions = []
      this.editedRuleData = []
      this.editedActionData = []

      this.$nextTick(() => {
        // Clear validator
        this.$validator.reset()
        // Push a initial ids to display one rule and action component
        this.rules.push({ id: 1 })
        this.rule_actions.push({ id: 1 })
      })

      if (isMsg) {
        this.reportSuccess('Form was reseted')
      }
    }
  },
  provide () {
    // Validation
    return { parentValidator: this.$validator }
  },
  watch: {
    'filters.single': function () {
      this.parseEditedData()
    }
  },
  mounted () {
    if (this.editing) this.parseEditedData()
  }
}
</script>
